import React, { useState } from 'react';
import styled from '@emotion/styled';
import { withAuthenticationRequired } from 'react-oidc-context';
import { ThemeProvider } from './styles/ThemeProvider';
import { FileValidatorPage } from './pages/FileValidatorPage';
import { MappingPage } from './pages/MappingPage';
import { AppHeader } from './components/Header';
import { LoadingPage } from './components/LoadingPage';
import { AccessDeniedPage } from './components/AccessDeniedPage';
import { useGuardian } from './authentication/GuardianProvider';
import { PageBody } from '@uhg-abyss/web/ui/PageBody';
import { Footer } from '@uhg-abyss/web/ui/Footer';
import './styles/global.css';

type TabId = 'validate' | 'mapping';

const PageContainer = styled.div`
  min-height: 100vh;
  background: #f8f9fa;
`;

const TabBar = styled.nav`
  width: 100%;
  max-width: 1760px;
  margin: 0 auto;
  padding: 24px clamp(20px, 3vw, 48px) 0;
  display: flex;
  gap: 0;
  border-bottom: 2px solid #e1e8ed;

  @media (max-width: 640px) {
    padding: 16px 16px 0;
    overflow-x: auto;
  }
`;

const Tab = styled.button<{ active: boolean }>`
  padding: 14px 28px;
  font-size: 15px;
  font-weight: 600;
  border: none;
  background: ${props => (props.active ? 'white' : 'transparent')};
  color: ${props => (props.active ? '#002677' : '#5a6c7d')};
  border-bottom: 3px solid ${props => (props.active ? '#ff612b' : 'transparent')};
  cursor: pointer;
  transition: all 0.2s ease;
  position: relative;
  top: 2px;
  border-radius: 8px 8px 0 0;
  display: flex;
  align-items: center;
  gap: 8px;

  &:hover {
    color: #002677;
    background: ${props => (props.active ? 'white' : '#f0f4f8')};
  }
`;

const TabIcon = styled.span`
  display: flex;
  align-items: center;
`;

function App() {
  const [activeTab, setActiveTab] = useState<TabId>('validate');
  const {
    isLoading: isGuardianLoading,
    isAuthorized,
    error: guardianError,
    diagnostics,
    refresh,
  } = useGuardian();

  // Sentinel has authenticated by this point (withAuthenticationRequired);
  // Guardian still has to say whether this user may use the application.
  if (isGuardianLoading) {
    return <LoadingPage />;
  }

  if (!isAuthorized) {
    return (
      <ThemeProvider>
        <PageContainer>
          <AppHeader />
          <AccessDeniedPage
            error={guardianError}
            diagnostics={diagnostics}
            onRetry={refresh}
            serviceUnavailable={guardianError === 'Authentication service is temporarily unavailable'}
          />
        </PageContainer>
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider>
      <PageContainer>
        <AppHeader />

        <PageBody fullWidth>
          <TabBar>
            <Tab active={activeTab === 'validate'} onClick={() => setActiveTab('validate')}>
              <TabIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M22 11.08V12a10 10 0 11-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </TabIcon>
              Validate HPS File
            </Tab>
            <Tab active={activeTab === 'mapping'} onClick={() => setActiveTab('mapping')}>
              <TabIcon>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="6" cy="6" r="3" />
                  <circle cx="18" cy="18" r="3" />
                  <path d="M6 21V9a9 9 0 009 9" />
                </svg>
              </TabIcon>
              Map to HPS Data Load File
            </Tab>
          </TabBar>

          {activeTab === 'validate' && <FileValidatorPage />}
          {activeTab === 'mapping' && <MappingPage />}
        </PageBody>

        <Footer>
          <Footer.SubFooter
            copyright="Optum, Inc."
            links={[
              { label: 'Privacy Policy', href: '#' },
              { label: 'Terms of Use', href: '#' },
              { label: 'Accessibility', href: '#' },
            ]}
          />
        </Footer>
      </PageContainer>
    </ThemeProvider>
  );
}

export default withAuthenticationRequired(App, {
  OnRedirecting: () => <LoadingPage />,
});
